import React from 'react';

import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import Box from '@mui/material/Box';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';

import SeriesTransactionPickerContent from './SeriesTransactionPickerContent';

export type SelectSeriesTransactionDialogProps = {
  open: boolean;
  title: string;
  subtitle?: string;
  selectorProps?: Record<string, unknown>;
  onTransactionSelected: (transaction?: TransactionProps) => void;
  onClose: () => void;
  rootModalDrawer?: RootModalDrawerOptions;
};

/**
 * Dialog/drawer chrome around the shared Series transaction-picker content
 * (see `SeriesTransactionPickerContent`) for the Create Series "pick an
 * existing transaction" flow. Mirrors `TransactionItemSelectDialog`'s
 * rootModalDrawer-conditional chrome: when hosted in a RootModal drawer,
 * renders bare drawer-flavored content — RootModal supplies the actual drawer
 * shell externally — otherwise renders a normal `StdDialog`.
 */
export default function SelectSeriesTransactionDialog({
  open,
  title,
  subtitle,
  selectorProps,
  onTransactionSelected,
  onClose,
  rootModalDrawer,
}: SelectSeriesTransactionDialogProps): React.JSX.Element | null {
  const content = (
    <SeriesTransactionPickerContent
      subtitle={subtitle}
      selectorProps={selectorProps}
      onTransactionSelected={onTransactionSelected}
      onClose={onClose}
    />
  );

  if (rootModalDrawer) {
    if (!open) {
      return null;
    }

    return (
      <Box aria-label={title} role='dialog' sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <StdDialogTitle
          variant='drawer'
          id='select-series-transaction-dialog-title'
          onClose={onClose}
          showCloseButton
          spacious
        >
          {title}
        </StdDialogTitle>
        {content}
      </Box>
    );
  }

  return (
    <StdDialog id='select-series-transaction-dialog' open={open} onClose={onClose} title={title} maxWidth='md'>
      {content}
    </StdDialog>
  );
}
